import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CustomDateRangePicker, CustomSelect, SearchInput } from '@/components';
import { useMobileScrollLock, useOutsideClick } from '@/common/hooks';
import { FilterIcon } from '../../components/Icons';
import { getCompanyEmployees } from '../../api';
import datePickerCalendarIcon from '../../assets/Inquiry/calendar.png';
import importIcon from '../../assets/importIcon.png';
import {
  REMINDER_FILTER_FIELDS,
  REMINDER_TYPE_OPTIONS,
  REMINDER_STATUS_FILTER_OPTIONS,
  REMINDER_SORT_OPTIONS,
} from '../utils';

const CHIP_SELECT_CLASS =
  'h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none';

function FilterChipRemoveButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Remove ${label} filter`}
      className="text-[#868686] hover:text-[#1F2241] shrink-0"
    >
      <svg
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
        aria-hidden
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>
  );
}

export function RemindersFilterToolbar({
  companyId,
  search,
  onSearchChange,
  typeFilter,
  onTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  assignedEmployeeId,
  onAssignedEmployeeChange,
  dateFrom,
  dateTo,
  onDateRangeChange,
  sortKey,
  onSortKeyChange,
  activeFilterFields,
  onAddFilterField,
  onRemoveFilterField,
  onClearAllFilters,
  canImport,
  onImportClick,
}) {
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');
  const filterMenuRef = useRef(null);

  useMobileScrollLock(isFilterMenuOpen);

  useEffect(() => {
    const timer = setTimeout(
      () => setDebouncedEmployeeSearch(employeeSearch),
      300,
    );
    return () => clearTimeout(timer);
  }, [employeeSearch]);

  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );

  const { data: employeesData, isFetching: isEmployeesLoading } = useQuery({
    queryKey: [
      'company-employees-reminders',
      companyId,
      debouncedEmployeeSearch,
    ],
    queryFn: () =>
      getCompanyEmployees(companyId, {
        search: debouncedEmployeeSearch || undefined,
      }),
    enabled: !!companyId && activeFilterFields.includes('assigned'),
  });

  const employees = Array.isArray(employeesData)
    ? employeesData
    : employeesData?.results || [];

  const employeeOptions = [
    { value: '', label: 'All assignees' },
    ...employees.map((e) => ({
      value: String(e.id),
      label: e.name || e.user?.full_name || `Employee ${e.id}`,
    })),
  ];

  const availableFilterFields = REMINDER_FILTER_FIELDS.filter(
    (f) => !activeFilterFields.includes(f.key),
  );

  return (
    <div className="flex flex-col gap-3 w-full shrink-0 mb-3 font-sf-pro-display">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full min-w-0">
        <SearchInput
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search contact, phone, title..."
          className="w-full min-w-0 lg:flex-1 lg:max-w-md order-2 lg:order-1"
          inputClassName="h-[44px]"
        />

        <div className="flex items-center gap-2 sm:gap-3 w-full lg:w-auto lg:ml-auto min-w-0 order-1 lg:order-2">
          <div className="relative shrink-0" ref={filterMenuRef}>
            <button
              type="button"
              onClick={() => setIsFilterMenuOpen((prev) => !prev)}
              aria-expanded={isFilterMenuOpen}
              aria-haspopup="menu"
              className={`relative h-[44px] px-4 rounded-[12px] text-sm sm:text-[15px] font-semibold flex items-center gap-2 transition-colors shrink-0 focus:outline-none ${
                isFilterMenuOpen || activeFilterFields.length > 0
                  ? 'bg-primary text-white hover:bg-primary-hover'
                  : 'bg-[#F5F5F5] text-[#1F2241] hover:brightness-95'
              }`}
            >
              <FilterIcon className="w-4 h-4" />
              Filter
            </button>

            {isFilterMenuOpen && (
              <div
                role="menu"
                data-overlay-container="true"
                style={{ overscrollBehavior: 'contain' }}
                className="absolute left-0 lg:left-auto lg:right-0 top-[52px] z-40 w-56 max-w-[calc(100vw-2rem)] bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2"
              >
                <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                  Filter by
                </p>
                {availableFilterFields.length > 0 ? (
                  availableFilterFields.map((field) => (
                    <button
                      key={field.key}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        onAddFilterField(field.key);
                        setIsFilterMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                    >
                      {field.label}
                    </button>
                  ))
                ) : (
                  <p className="px-4 py-2 text-sm text-text-muted">
                    All filters added
                  </p>
                )}
              </div>
            )}
          </div>

          {canImport && (
            <button
              type="button"
              onClick={onImportClick}
              className="inline-flex h-[44px] items-center justify-center gap-[6px] rounded-[12px] px-4 text-sm sm:text-[15px] font-medium font-sf-pro-display text-[#1F2241] transition-colors focus:outline-none hover:brightness-95 shrink-0"
              style={{ backgroundColor: '#F5F5F5' }}
            >
              Import
              <img src={importIcon} alt="" className="w-6 h-6 object-contain" />
            </button>
          )}
        </div>
      </div>

      {activeFilterFields.length > 0 && (
        <div className="flex flex-wrap items-center gap-3">
          {activeFilterFields.map((key) => {
            const field = REMINDER_FILTER_FIELDS.find((f) => f.key === key);
            if (!field) return null;

            return (
              <div
                key={key}
                className="min-h-10 flex flex-wrap items-center gap-2 py-1.5 pl-3 pr-2 rounded-xl border border-[#EBF4EA] bg-white max-w-full"
              >
                <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap">
                  {field.label}
                </span>
                <span className="text-sm text-[#868686]">is</span>

                {key === 'type' && (
                  <CustomSelect
                    value={typeFilter}
                    onChange={onTypeFilterChange}
                    options={REMINDER_TYPE_OPTIONS}
                    dropdownWidth={200}
                    buttonClassName={CHIP_SELECT_CLASS}
                    textSizeClass="text-sm"
                  />
                )}

                {key === 'status' && (
                  <CustomSelect
                    value={statusFilter}
                    onChange={onStatusFilterChange}
                    options={[
                      { value: '', label: 'Select status' },
                      ...REMINDER_STATUS_FILTER_OPTIONS,
                    ]}
                    searchable
                    dropdownWidth={180}
                    buttonClassName={CHIP_SELECT_CLASS}
                    textSizeClass="text-sm"
                  />
                )}

                {key === 'assigned' && (
                  <CustomSelect
                    value={assignedEmployeeId}
                    onChange={onAssignedEmployeeChange}
                    options={employeeOptions}
                    searchable
                    searchPlaceholder="Search employee..."
                    onSearchChange={setEmployeeSearch}
                    isLoading={isEmployeesLoading}
                    dropdownWidth={220}
                    buttonClassName={CHIP_SELECT_CLASS}
                    textSizeClass="text-sm"
                  />
                )}

                {key === 'dateRange' && (
                  <CustomDateRangePicker
                    startDate={dateFrom}
                    endDate={dateTo}
                    onApply={onDateRangeChange}
                    onClear={() => onDateRangeChange('', '')}
                    triggerClassName="h-8 px-2 rounded-lg border-0 bg-[#F5F5F5] text-sm justify-between"
                    iconSrc={datePickerCalendarIcon}
                  />
                )}

                {key === 'sort' && (
                  <CustomSelect
                    value={sortKey}
                    onChange={onSortKeyChange}
                    options={REMINDER_SORT_OPTIONS.map(({ value, label }) => ({
                      value,
                      label,
                    }))}
                    dropdownWidth={220}
                    buttonClassName={CHIP_SELECT_CLASS}
                    textSizeClass="text-sm"
                  />
                )}

                <FilterChipRemoveButton
                  label={field.label}
                  onClick={() => onRemoveFilterField(key)}
                />
              </div>
            );
          })}

          <button
            type="button"
            onClick={onClearAllFilters}
            className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
